<script setup lang="ts">
const props = withDefaults(defineProps<{
  language?: string
  base_url?: string
  b2b_footer?: boolean
  footerType?: 'auth' | 'b2b' | 'default'
}>(), {
  language: 'en',
  footerType: 'default',
})

const { language, b2b_footer: b2bFooter } = toRefs(props);

const config = useRuntimeConfig();
const baseUrl = computed(() => props.base_url ?? config.public.wadPlatformBaseUrl);

const defaultFooterItems: FooterColumn[] = [
  {
    header: 'Events',
    items: [
      { text: 'Live', url: `${baseUrl.value}/live` },
      { text: 'World Congress', url: 'https://www.wearedevelopers.com/world-congress/' },
      { text: 'CODE100', url: 'https://www.wearedevelopers.com/events/code100' }
    ]
  },
  {
    header: 'Developers',
    items: [
      { text: 'Jobs', url: `${baseUrl.value}/${language.value}/jobs` },
      { text: 'Companies', url: `${baseUrl.value}/${language.value}/companies` },
      { text: 'Magazine', url: `${baseUrl.value}/magazine` },
      { text: 'Tech Talks', url: `${baseUrl.value}/${language.value}/videos` },
      { text: 'Community', url: 'https://www.wearedevelopers.com/about/community' },
      { text: 'Newsletter', url: 'https://www.wearedevelopers.com/about/newsletter' }
    ]
  }
]

const b2bFooterItems: FooterColumn[] = [
  {
    header: 'Solutions',
    items: [
      { text: 'Employer Branding', url: `${baseUrl.value}/${language.value}/talent-marketing-membership` },
      { text: 'Tech Recruiting', url: `${baseUrl.value}/${language.value}/developer-recruiting-membership` },
      { text: 'Product Marketing', url: `${baseUrl.value}/${language.value}/product-marketing-membership` },
      { text: 'Event Partnerships', url: `${baseUrl.value}/${language.value}/event-partnerships` },
      { text: 'Post a job', url: `${baseUrl.value}/${language.value}/company/post-job` }
    ]
  },
  {
    header: 'Products',
    items: [
      { text: 'Talent Marketing Membership', url: `${baseUrl.value}/${language.value}/talent-marketing-membership` },
      { text: 'Product Marketing Membership', url: `${baseUrl.value}/${language.value}/product-marketing-membership` },
      { text: 'WeAreDevelopers World Congress', url: 'https://www.wearedevelopers.com/world-congress' },
      { text: 'Job Platform', url: `${baseUrl.value}/${language.value}/company/post-job` }
    ]
  },
  {
    header: 'Insights',
    items: [
      { text: 'Resources', url: `${baseUrl.value}/business/resources` },
      { text: 'Developer survey', url: `${baseUrl.value}/business/developer-survey-24` },
      { text: 'Salary calculator', url: `${baseUrl.value}/business/developer-salary-calculator` },
      { text: 'Newsletter', url: `${baseUrl.value}/business/newsletter` },
      { text: 'Blog', url: `${baseUrl.value}/business/blog` }
    ]
  }
]

const social: FooterColumn = {
  header: 'Social',
  items: [
    { text: 'Facebook', url: 'https://www.facebook.com/wearedevelopers' },
    { text: 'Instagram', url: 'https://www.instagram.com/_wearedevelopers/' },
    { text: 'X', url: 'https://twitter.com/WeAreDevs' },
    { text: 'YouTube', url: 'https://www.youtube.com/channel/UCSD0dLRGQk_T-D3RvpM5aFQ' },
    { text: 'LinkedIn', url: 'https://www.linkedin.com/company/wearedevelopers.org' }
  ]
}

const wad: FooterColumn = {
  header: 'WeAreDevelopers',
  items: [
    { text: 'About', url: 'https://www.wearedevelopers.com/about' },
    { text: 'Team', url: 'https://www.wearedevelopers.com/about#team' },
    { text: 'Career', url: 'https://www.wearedevelopers.com/about#career' },
    { text: 'Contact', url: 'https://www.wearedevelopers.com/about/contact-us' },
    { text: 'Press', url: 'https://www.wearedevelopers.com/about/press' },
    { text: 'Legal', url: 'https://www.wearedevelopers.com/about/legal' },
    { text: 'Cookies', url: 'javascript:Truendo.openCookieSettings()' },
    { text: 'Imprint', url: 'https://www.wearedevelopers.com/about/legal/imprint' }
  ]
}

const authFooterItems: FooterColumn[] = [
  {
    header: 'WeAreDevelopers',
    items: [
      { text: 'Legal', url: 'https://www.wearedevelopers.com/about/legal' },
      { text: 'Cookies', url: 'javascript:Truendo.openCookieSettings()' },
      { text: 'Imprint', url: 'https://www.wearedevelopers.com/about/legal/imprint' }
    ]
  }
]

const currentYear = new Date().getFullYear();

const footerColumns = computed<FooterColumn[]>(() => {
  if (props.footerType === 'auth') {
    return [...authFooterItems]
  }
  const base = props.footerType === 'b2b' ? b2bFooterItems : defaultFooterItems
  return [...base, social, wad]
})

const authLinks = computed(() =>
    props.footerType === 'auth'
        ? footerColumns.value.flatMap(col => col.items)
        : []
)

</script>

<template>
  <footer class="wad4-footer" :class="{ 'wad4-footer--auth': props.footerType === 'auth' }">

    <div v-if="props.footerType === 'auth'" class="wad4-footer--auth-container">
      <span class="wad4-footer__copyright">
        <small>
          <span aria-hidden="true">©</span>
          <time :datetime="`${currentYear}-01-01`">{{ currentYear }}</time>
          WeAreDevelopers
        </small>
      </span>
      <a
          v-for="(item, idx) in authLinks"
          :key="idx"
          :href="item.url"
          class="wad4-footer--auth-container__link"
          :aria-label="item.ariaLabel || item.text"
          :target="item.external ? '_blank' : null"
          :rel="item.external ? 'noopener noreferrer' : null"
      >
        {{ item.text }}
      </a>
    </div>

    <nav v-else class="wad4__container wad4__container--columns wad4-footer__columns" aria-label="Footer navigation">
      <section
          v-for="(column, colIdx) in footerColumns"
          :key="colIdx"
          class="wad4-footer__column"
          :aria-labelledby="`footer-col-${colIdx}`"
      >
        <h2 :id="`footer-col-${colIdx}`" class="wad4-footer__column-header">{{ column.header }}</h2>
        <a
            v-for="(item, itemIdx) in column.items"
            :key="itemIdx"
            :href="item.url"
            class="wad4-footer__link"
            :aria-label="item.ariaLabel || item.text"
            :target="item.external ? '_blank' : null"
            :rel="item.external ? 'noopener noreferrer' : null"
        >
          {{ item.text }}
        </a>
      </section>
    </nav>

  </footer>

</template>

<style lang="scss">
.wad4-footer {
  width: 100%;
  font-family: $wad4-default-font-sans;
  color: $wad4-c-text-text-primary;
  background: $wad4-c-background-bg-primary;
  display: flex;
  flex-direction: column;
  align-items: center;
  align-self: stretch;
  padding-top: $wad4-viewport-section-padding;
  padding-bottom: $wad4-viewport-section-padding;
  row-gap: $wad4-spacing-4xl;
  border-top: 2px solid $wad4-c-border-border-subtle;

  &--auth {
    border-top: none;
    padding: unset;
  }

  &__columns.wad4__container--columns {
    row-gap: $wad4-spacing-4xl;
    padding-bottom: $wad4-spacing-4xl;
  }

  &__column {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: $wad4-spacing-xl;
    flex: 1 0 0;
    flex-wrap: wrap;
    min-width: $wad4-col-2;
    max-width: $wad4-col-3;
  }

  &__column-header {
    font-weight: $wad4-font-weight-bold;
    font-size: 14px;
    line-height: 20px;
    margin: 0;
  }

  &__link {
    color: $wad4-c-text-text-primary !important;
    text-decoration: none;
    font-weight: $wad4-font-weight-regular;
    font-size: 14px;
    line-height: 20px;
  }

  &__copyright {
    text-align: center;
    font-size: 14px;
    line-height: 14px
  }

  &--auth-container {
    display: flex;
    align-items: center;
    gap: var(--container-spacing, 24px);
    flex-wrap: wrap;
    color: $wad4-c-text-text-primary;

    &__copyright {
      color: $wad4-c-text-text-primary;
      font-size: 12px;
    }

    &__link{
      color: $wad4-c-text-text-primary;
      font-size: 12px;
      text-decoration: none;
    }

  }
}
</style>
